import { bossInvasions, jewelInvasions, rewardTotals, activityEvents, INVASION_BG } from '../invasions'
import { invasionMapNames } from './maps'
import { buildEventEntry, buildItemEntry, buildMechanicEntry, parseRewardLine } from '../../utils/guideTemplates'
import { joinList, normalize, slugify } from '../../utils/text'
import { JEWEL_MAIN_SOURCE, JEWEL_RARE_NOTE, staffJewelEntryIds } from './jewelSources'
import type { EntityRef, EntryLink, GuideEntry } from '../../types/guide'

/*
 * Fuente central de la guía. Las invasiones se construyen desde data/invasions.ts
 * (sin modificar esos datos). Las fichas de joyas e ítems calculan
 * automáticamente dónde se consiguen a partir de las recompensas.
 */

const INVASIONS = 'invasiones-bosses'
const SCHEDULE_TEXT = 'No hay horarios fijos publicados en esta guía. Presiona la letra H dentro del juego para consultarlos. También recordamos por Discord cuando se aproxima una invasión.'

// ── Ítems que pueden enlazarse desde las recompensas ─────────────────────────
const jewelNames = Array.from(new Set(jewelInvasions.flatMap((j) => j.drops.map((d) => d.jewel))))

const jewelAliases: Record<string, string[]> = {
  'jewel-of-chaos': ['Joya del Caos', 'Joya de Chaos', 'Chaos', 'JoC'],
  'jewel-of-soul': ['Joya del Alma', 'Soul', 'JoS'],
  'jewel-of-bless': ['Joya de la Bendición', 'Bless', 'JoB'],
  'jewel-of-life': ['Joya de la Vida', 'Life', 'JoL'],
  'jewel-of-creation': ['Joya de la Creación', 'Creation'],
}

/**
 * item_id del catálogo de cada ficha editorial de ítem. Es la llave de su imagen en toda la guía
 * (buscador, recompensas, enlaces y ficha): la imagen se resuelve por este ID, nunca por el nombre.
 */
const catalogItemIds: Record<string, string> = {
  'jewel-of-chaos': '12,15',
  'jewel-of-bless': '14,13',
  'jewel-of-soul': '14,14',
  'jewel-of-life': '14,16',
  'jewel-of-creation': '14,22',
  'wizards-ring': '13,20',
}

type ItemDef = { id: string; title: string; categoryId: string; kindLabel: string; aliases: string[]; tags: string[] }

const itemDefs: ItemDef[] = [
  ...jewelNames.map((name) => ({
    id: slugify(name),
    title: name,
    categoryId: 'joyas-drops',
    kindLabel: 'Joya',
    aliases: jewelAliases[slugify(name)] ?? [],
    tags: ['joya'],
  })),
  {
    id: 'wizards-ring',
    title: 'Wizard’s Ring',
    categoryId: 'items-equipo',
    kindLabel: 'Accesorio',
    aliases: ['Wizard Ring', 'Wizards Ring', 'Anillo del mago'],
    tags: ['anillo', 'accesorio'],
  },
]

const resolveItem = (label: string) => itemDefs.find((d) => normalize(label).includes(normalize(d.title)))?.id

/** Entidad (item_id) de una recompensa publicada, a través de su ficha editorial. */
export const itemEntityForLabel = (label: string): EntityRef | undefined => {
  const id = resolveItem(label)
  const itemId = id ? catalogItemIds[id] : undefined
  return itemId ? { type: 'item', id: itemId } : undefined
}

// ── Invasiones ───────────────────────────────────────────────────────────────
const invasionAliases: Record<string, string[]> = {
  'death-king': ['Skeleton', 'Esqueleto', 'Rey de la muerte'],
  'red-dragon': ['Dragón rojo', 'Dragón'],
  'white-wizard': ['Mago blanco', 'Soldados', 'Arqueros'],
  'new-year': ['Año Nuevo'],
  rabbits: ['Conejos', 'Conejo', 'Rabbit'],
  summer: ['Verano'],
}

const invasionLocation: Record<string, string> = {
  ...Object.fromEntries(bossInvasions.map((b) => [b.id, b.location])),
  ...Object.fromEntries(jewelInvasions.map((j) => [j.id, `Un mapa elegido entre ${j.maps}`])),
}

const bossEntries = bossInvasions.map((b) =>
  buildEventEntry({
    id: b.id,
    categoryId: INVASIONS,
    kindLabel: 'Invasión con jefe',
    title: b.name,
    summary: `Invasión con jefe. ${b.highlight}.`,
    image: b.poster.src,
    aliases: invasionAliases[b.id],
    tags: ['invasión', 'jefe', 'boss'],
    location: b.location,
    mapNames: invasionMapNames(b.id),
    monsters: b.spawn,
    frequency: `${b.perDay} veces al día`,
    rewards: b.rewards.map((r) => ({ who: r.who, lines: r.items.map((item) => parseRewardLine(item, resolveItem)) })),
    scheduleText: SCHEDULE_TEXT,
    scheduleEntryId: 'horarios-invasiones',
    related: ['invasiones'],
  }),
)

const jewelInvasionEntries = jewelInvasions.map((j) =>
  buildEventEntry({
    id: j.id,
    categoryId: INVASIONS,
    kindLabel: 'Invasión de joyas',
    title: j.name,
    summary: `Invasión de joyas: ${j.monsters} monstruos por aparición y cada uno entrega 1 joya garantizada.`,
    image: j.poster.src,
    aliases: invasionAliases[j.id],
    tags: ['invasión', 'joyas'],
    location: invasionLocation[j.id],
    mapNames: invasionMapNames(j.id),
    monsters: `${j.monsters} monstruos`,
    frequency: `${j.perDay} veces al día`,
    rewards: [
      {
        who: 'Cada monstruo',
        note: 'Entrega 1 joya garantizada. El tipo de joya depende de la probabilidad:',
        lines: j.drops.map((d) => ({ label: d.jewel, chance: d.pct, entryId: resolveItem(d.jewel) })),
      },
    ],
    scheduleText: SCHEDULE_TEXT,
    scheduleEntryId: 'horarios-invasiones',
    related: ['invasiones'],
  }),
)

const invasionEntries = [...bossEntries, ...jewelInvasionEntries]

// ── Relaciones calculadas: dónde se consigue cada ítem ───────────────────────
export const itemSourceMap: Record<string, EntryLink[]> = {}
invasionEntries.forEach((e) => {
  e.sections.forEach((s) =>
    s.blocks.forEach((b) => {
      if (b.type !== 'rewards') return
      b.groups.forEach((g) =>
        g.lines.forEach((l) => {
          if (!l.entryId) return
          if (!itemSourceMap[l.entryId]) itemSourceMap[l.entryId] = []
          itemSourceMap[l.entryId].push({
            entryId: e.id,
            sectionId: s.id,
            who: g.who,
            chance: l.chance,
            guaranteed: l.guaranteed,
            note: g.note ? 'Cada monstruo entrega 1 joya garantizada.' : undefined,
          })
        }),
      )
    }),
  )
})

const titleOf = (id: string) => invasionEntries.find((e) => e.id === id)?.title ?? id

const itemEntries = itemDefs.map((d) => {
  const sources = itemSourceMap[d.id] ?? []
  const where = Array.from(new Set(sources.map((s) => titleOf(s.entryId))))
  return buildItemEntry({
    id: d.id,
    categoryId: d.categoryId,
    kindLabel: d.kindLabel,
    title: d.title,
    summary: staffJewelEntryIds.has(d.id)
      ? `${JEWEL_MAIN_SOURCE}${where.length ? ` ${joinList(where)}.` : ''} ${JEWEL_RARE_NOTE}`
      : where.length
        ? `Según la información publicada, se obtiene en: ${joinList(where)}.`
        : 'Ficha en preparación.',
    aliases: d.aliases,
    tags: d.tags,
    entity: catalogItemIds[d.id] ? { type: 'item', id: catalogItemIds[d.id] } : undefined,
    sources,
  })
})

// ── Mecánicas ────────────────────────────────────────────────────────────────
const scheduleEntry = buildMechanicEntry({
  id: 'horarios-invasiones',
  categoryId: INVASIONS,
  kindLabel: 'Mecánica',
  title: 'Horarios de las invasiones',
  summary: 'Se consultan dentro del juego presionando la letra H.',
  aliases: ['Tecla H', 'Cuándo son las invasiones', 'Hora de la invasión'],
  tags: ['horario', 'invasión'],
  explanation: [
    'Las invasiones no tienen horarios fijos publicados en esta guía: se consultan dentro del juego.',
    'También recordamos por Discord cuando se aproxima una invasión.',
  ],
  steps: ['Dentro del juego, presiona la letra H.', 'Consulta los horarios de las invasiones.', 'Mantente pendiente de los recordatorios en Discord.'],
  related: ['invasiones'],
})

const dropEntry = buildMechanicEntry({
  id: 'drop-farmeo',
  categoryId: 'joyas-drops',
  kindLabel: 'Mecánica',
  title: 'Drop de joyas e ítems Excellent en el farmeo',
  summary: 'El drop habitual se redujo: las cosas preciadas se salen a buscar.',
  aliases: ['Drop en el spot', 'Drop reducido', 'Rates de drop'],
  tags: ['drop', 'farmeo', 'excellent', 'joyas'],
  explanation: [
    'El drop de joyas e ítems Excellent en el farmeo habitual se redujo. Queremos que estos objetos conserven su importancia y que conseguirlos también implique salir del spot, participar y competir.',
    'En tu spot seguirás teniendo oportunidades de un golpe de suerte, y las cajas también conservan su componente de azar.',
    'Su valor comercial dependerá de su utilidad, su rareza y de lo que otros jugadores estén dispuestos a pagar.',
  ],
  related: ['invasiones'],
})

// Los mapas ahora tienen su propia ficha en el explorador (/guia/mapas), construida con maps.csv.

// ── Artículo completo de invasiones (página propia ya existente) ─────────────
const totalJewels = rewardTotals.reduce((s, r) => s + r.jewels, 0)
const totalItems = rewardTotals.reduce((s, r) => s + r.items, 0)

const invasionsArticle: GuideEntry = {
  id: 'invasiones',
  categoryId: INVASIONS,
  kind: 'article',
  kindLabel: 'Guía completa',
  title: 'Invasiones',
  summary: 'Dónde aparecen, qué recompensas entregan y por qué importan para tu progreso.',
  aliases: ['Guía de invasiones', 'Invasión'],
  tags: ['invasión', 'jefe', 'joyas', 'recompensas'],
  image: INVASION_BG,
  status: 'available',
  customPath: '/guia/invasiones-bosses/invasiones',
  sections: [],
  related: ['horarios-invasiones'],
  searchSections: [
    { id: 'horarios', title: '¿Cuándo vienen las invasiones?', text: SCHEDULE_TEXT },
    { id: 'jefes', title: 'Invasiones con jefe', text: bossInvasions.map((b) => `${b.name}: ${b.highlight}. ${b.location}. ${b.spawn}.`).join(' ') },
    { id: 'joyas', title: 'Invasiones de joyas', text: jewelInvasions.map((j) => `${j.name}: ${j.maps}. ${j.drops.map((d) => `${d.pct}% ${d.jewel}`).join(', ')}.`).join(' ') },
    {
      id: 'recompensas',
      title: '¿Cuántas recompensas habrá en juego?',
      text: `Si se derrotan todos los objetivos: ${totalJewels} joyas y ${totalItems} ítems NO Excellent por día y por subservidor. Con ambos servidores principales: ${totalJewels * 2} joyas y ${totalItems * 2} ítems.`,
    },
    { id: 'importa', title: 'Cada invasión tiene que importar', text: 'Desde un Rabbit, pasando por un Dragón Dorado, hasta una Medusa: cada objetivo debe tener su lugar. Comenzamos potenciando las invasiones básicas mientras la comunidad recorre el camino hacia su primer reset.' },
    { id: 'preciadas', title: 'Las cosas preciadas se salen a buscar', text: 'El drop de joyas e ítems Excellent en el farmeo habitual se redujo. Las cajas conservan su componente de azar. Su valor comercial dependerá de su utilidad y rareza.' },
    { id: 'actividad', title: 'Más actividad. Más estrategia. Más competencia.', text: `Las invasiones se suman a ${joinList(activityEvents.filter((e) => e !== 'Invasiones'))}.` },
  ],
}

// ── Invasión de Dorados (invasion:2): ficha propia, publicada con autorización del staff ──
const goldenEntry: GuideEntry = {
  id: 'golden',
  categoryId: INVASIONS,
  kind: 'article',
  kindLabel: 'Invasión',
  title: 'Invasión de Dorados',
  summary: '19 monstruos dorados, 4 veces al día. Entregan cajas Kundun +1 a +5.',
  aliases: ['Dorados', 'Golden', 'Golden Invasion', 'Invasión Golden', 'Dragón dorado', 'Great Golden Dragon', 'Golden Budge Dragon', 'Golden Derkon', 'Dónde salen los dorados', 'Monstruos dorados'],
  tags: ['invasión', 'dorados', 'golden', 'kundun', 'cajas', 'kundun +1', 'kundun +2', 'kundun +3', 'kundun +4', 'kundun +5'],
  status: 'available',
  updatedAt: '08/10/2026',
  customPath: '/guia/dorados',
  sections: [],
  related: ['invasiones'],
  searchSections: [
    { id: 'horarios', title: 'Horarios de los Dorados', text: '4 veces al día, cada 6 horas: 00:00, 06:00, 12:00 y 18:00 hora de Venezuela. Duran 30 minutos. Confirma los horarios actuales con la tecla H.' },
    { id: 'monstruos', title: 'Dónde salen los dorados', text: 'Lorencia, Noria, Devias, Elbeland, Dungeon, Atlans, Lost Tower, Tarkan, Aida, Icarus, Kanturu 1, Kanturu 2, Swamp of Calmness, Raklion 1. Golden Derkon y Great Golden Dragon aparecen en uno de sus mapas posibles.' },
    { id: 'cajas', title: 'Dónde consigo Kundun +1, +2, +3, +4 y +5', text: 'Cada dorado entrega una caja Kundun. Kundun +1: Budge Dragon, Goblin, Titan, Soldier, Rabbit, Dark Knight dorados. Kundun +2: Lizard King, Vepar, Devil. Kundun +3: Derkon, Tantalos, Wheel, Stone Golem, Crust. Kundun +4: Satyros, Twin Tail. Kundun +5: Napin, Iron Knight. Great Golden Dragon: 5 cajas, cada una Kundun +4 o +5.' },
  ],
}

export const guideEntries: GuideEntry[] = [
  invasionsArticle,
  goldenEntry,
  ...invasionEntries,
  scheduleEntry,
  ...itemEntries,
  dropEntry,
]
